// src/crm/templates/useMessageTemplates.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { ContactType, MessageChannel, MessageTemplate, TemplateLanguage } from '../../lib/types'

const templateKeys = {
  all: ['message-templates'] as const,
  list: (appId: string) => ['message-templates', 'list', appId] as const,
}

// `| undefined` explicite : exactOptionalPropertyTypes est activé.
export type TemplateFilters = {
  channel?: MessageChannel | '' | undefined
  contactType?: ContactType | '' | undefined
  language?: TemplateLanguage | '' | undefined
}

/**
 * Liste les templates de l'app active. L'API exige `appId` : la requête reste
 * inactive tant qu'aucune app n'est sélectionnée.
 */
export function useMessageTemplates(appId: string | null, filters: TemplateFilters = {}) {
  const params = new URLSearchParams()
  if (appId) params.set('appId', appId)
  if (filters.channel) params.set('channel', filters.channel)
  if (filters.contactType) params.set('contactType', filters.contactType)
  if (filters.language) params.set('language', filters.language)
  return useQuery<MessageTemplate[], Error>({
    queryKey: [
      ...templateKeys.list(appId ?? ''),
      filters.channel ?? '',
      filters.contactType ?? '',
      filters.language ?? '',
    ],
    queryFn: () =>
      api
        .get<{ templates: MessageTemplate[] }>(`/api/message-templates?${params.toString()}`)
        .then((r) => r.templates),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 2,
  })
}

export type CreateTemplateInput = {
  appId: string
  name: string
  channel: MessageChannel
  contactType?: ContactType | null | undefined
  language?: TemplateLanguage | undefined
  subject?: string | null | undefined
  body: string
  sortOrder?: number | undefined
}

export type UpdateTemplateInput = Omit<Partial<CreateTemplateInput>, 'appId'>

export function useCreateMessageTemplate() {
  const queryClient = useQueryClient()
  return useMutation<MessageTemplate, Error, CreateTemplateInput>({
    mutationFn: (body) =>
      api.post<{ template: MessageTemplate }>('/api/message-templates', body).then((r) => r.template),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.all })
    },
  })
}

export function useUpdateMessageTemplate(id: string) {
  const queryClient = useQueryClient()
  return useMutation<MessageTemplate, Error, UpdateTemplateInput>({
    mutationFn: (body) =>
      api.put<{ template: MessageTemplate }>(`/api/message-templates/${id}`, body).then((r) => r.template),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.all })
    },
  })
}

export function useDeleteMessageTemplate() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.del<void>(`/api/message-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.all })
    },
  })
}
